import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import {
  ArrowLeft, DollarSign, Clock, CheckCircle, XCircle, Building2,
  Search, FileText, AlertTriangle, TrendingUp, CreditCard, Filter,
  Bell, Send, RefreshCw, Mail
} from 'lucide-react';

const statusConfig = {
  paid: { label: 'Payee', color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', icon: CheckCircle },
  pending: { label: 'En attente', color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', icon: Clock },
  cancelled: { label: 'Annulee', color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', icon: XCircle },
};

const KpiCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-start gap-3" data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center shrink-0`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold text-white">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const B2BPaymentsPage = () => {
  const { api } = useAuth();
  const { t, tError } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCompany, setFilterCompany] = useState('all');
  const [activeTab, setActiveTab] = useState('invoices');
  const [reminders, setReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/admin/b2b/payments/dashboard');
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const fetchReminders = async () => {
    setRemindersLoading(true);
    try {
      const res = await api.get('/admin/b2b/payments/reminders');
      setReminders(res.data);
    } catch (err) { console.error(err); }
    finally { setRemindersLoading(false); }
  };

  const sendReminder = async (invoiceId) => {
    setSendingReminder(invoiceId);
    try {
      const res = await api.post(`/admin/b2b/payments/send-reminder/${invoiceId}`);
      alert(res.data.message || 'Rappel envoye !');
      fetchReminders();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur envoi');
    } finally { setSendingReminder(null); }
  };

  const sendAllReminders = async () => {
    if (!window.confirm('Envoyer un rappel a toutes les entreprises avec des factures en retard ?')) return;
    setSendingAll(true);
    setReminderResult(null);
    try {
      const res = await api.post('/admin/b2b/payments/send-all-reminders');
      setReminderResult(res.data);
      fetchReminders();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur');
    } finally { setSendingAll(false); }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64 text-gray-400">Chargement...</div></Layout>;
  }

  if (!data) {
    return <Layout><div className="flex items-center justify-center h-64 text-red-400">Erreur de chargement</div></Layout>;
  }

  const { kpis, by_company, invoices, uninvoiced_repairs } = data;
  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

  // Filter invoices
  const filteredInvoices = invoices.filter(inv => {
    if (filterStatus !== 'all' && inv.payment_status !== filterStatus) return false;
    if (filterCompany !== 'all' && inv.company_name !== filterCompany) return false;
    if (search) {
      const q = search.toLowerCase();
      return inv.invoice_number.toLowerCase().includes(q) ||
        inv.repair_ref.toLowerCase().includes(q) ||
        inv.company_name.toLowerCase().includes(q) ||
        `${inv.brand} ${inv.model}`.toLowerCase().includes(q);
    }
    return true;
  });

  const companies = [...new Set(invoices.map(i => i.company_name))].sort();

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return d.slice(0, 10); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto fade-in space-y-6" data-testid="b2b-payments-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-gray-300 rounded-xl text-sm hover:bg-white/10 transition-colors"
              data-testid="payments-back-home">
              <ArrowLeft className="w-4 h-4" /> Accueil
            </button>
            <CreditCard className="w-6 h-6 text-emerald-400" />
            <h1 className="text-xl font-bold text-white">Suivi Paiements B2B</h1>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard icon={TrendingUp} label="Total facture" value={fmt(kpis.total_invoiced)}
            sub={`${kpis.invoice_count} factures`} color="bg-purple-600" />
          <KpiCard icon={CheckCircle} label="Paye" value={fmt(kpis.total_paid)}
            sub={`${kpis.paid_count} factures`} color="bg-green-600" />
          <KpiCard icon={Clock} label="En attente" value={fmt(kpis.total_pending)}
            sub={`${kpis.pending_count} factures`} color="bg-amber-600" />
          <KpiCard icon={XCircle} label="Annule" value={fmt(kpis.total_cancelled)}
            sub={`${kpis.cancelled_count} factures`} color="bg-red-600" />
          <KpiCard icon={AlertTriangle} label="Non facture" value={fmt(kpis.total_uninvoiced)}
            sub={`${uninvoiced_repairs.length} reparations`} color="bg-blue-600" />
        </div>

        {/* Company breakdown */}
        {by_company.length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wide">Par entreprise</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {by_company.map((c, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid={`company-card-${c.name}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-purple-400" />
                    <span className="text-white text-sm font-semibold truncate">{c.name}</span>
                    <span className="text-xs text-gray-500 ml-auto">{c.count} fact.</span>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-gray-400">Total: <b className="text-white">{fmt(c.total)}</b></span>
                    {c.paid > 0 && <span className="text-green-400">{fmt(c.paid)}</span>}
                    {c.pending > 0 && <span className="text-amber-400">{fmt(c.pending)}</span>}
                    {c.cancelled > 0 && <span className="text-red-400 line-through">{fmt(c.cancelled)}</span>}
                  </div>
                  {c.pending > 0 && (
                    <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: `${c.total > 0 ? (c.paid / c.total) * 100 : 0}%` }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-1">
          <button onClick={() => setActiveTab('invoices')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'invoices' ? 'bg-white/10 text-white border-b-2 border-emerald-400' : 'text-gray-500 hover:text-gray-300'}`}
            data-testid="tab-invoices">
            Factures ({invoices.length})
          </button>
          <button onClick={() => setActiveTab('uninvoiced')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'uninvoiced' ? 'bg-white/10 text-white border-b-2 border-blue-400' : 'text-gray-500 hover:text-gray-300'}`}
            data-testid="tab-uninvoiced">
            Non facturees ({uninvoiced_repairs.length})
          </button>
          <button onClick={() => { setActiveTab('reminders'); if (reminders.length === 0) fetchReminders(); }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-1.5 ${activeTab === 'reminders' ? 'bg-white/10 text-white border-b-2 border-amber-400' : 'text-gray-500 hover:text-gray-300'}`}
            data-testid="tab-reminders">
            <Bell className="w-3.5 h-3.5" /> Relances
          </button>
        </div>

        {/* INVOICES TAB */}
        {activeTab === 'invoices' && (
          <div className="space-y-3">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-emerald-500/50 focus:outline-none"
                  placeholder="Rechercher facture, ref, entreprise..." data-testid="payments-search" />
              </div>
              <div className="flex gap-2">
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                  className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none appearance-none cursor-pointer"
                  data-testid="payments-filter-status">
                  <option value="all" className="bg-gray-900">Tous les statuts</option>
                  <option value="paid" className="bg-gray-900">Payees</option>
                  <option value="pending" className="bg-gray-900">En attente</option>
                  <option value="cancelled" className="bg-gray-900">Annulees</option>
                </select>
                {companies.length > 1 && (
                  <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)}
                    className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none appearance-none cursor-pointer"
                    data-testid="payments-filter-company">
                    <option value="all" className="bg-gray-900">Toutes les entreprises</option>
                    {companies.map(c => <option key={c} value={c} className="bg-gray-900">{c}</option>)}
                  </select>
                )}
              </div>
            </div>

            {/* Invoice table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead>
                  <tr className="border-b border-white/10 text-gray-500 text-xs uppercase tracking-wide">
                    <th className="text-left py-3 px-3">Facture</th>
                    <th className="text-left py-3 px-3">Ref. reparation</th>
                    <th className="text-left py-3 px-3 hidden md:table-cell">Entreprise</th>
                    <th className="text-left py-3 px-3 hidden lg:table-cell">Appareil</th>
                    <th className="text-right py-3 px-3">Montant</th>
                    <th className="text-center py-3 px-3">Statut paiement</th>
                    <th className="text-left py-3 px-3 hidden md:table-cell">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map(inv => {
                    const sc = statusConfig[inv.payment_status] || statusConfig.pending;
                    const StatusIcon = sc.icon;
                    return (
                      <tr key={inv.invoice_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`invoice-row-${inv.invoice_number}`}>
                        <td className="py-3 px-3">
                          <span className="text-white font-mono font-semibold text-xs">{inv.invoice_number}</span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-purple-400 font-mono text-xs">{inv.repair_ref}</span>
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${inv.repair_status === 'Terminé' || inv.repair_status === 'Livré' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                            {inv.repair_status}
                          </span>
                        </td>
                        <td className="py-3 px-3 hidden md:table-cell text-gray-300 text-xs">{inv.company_name}</td>
                        <td className="py-3 px-3 hidden lg:table-cell text-gray-400 text-xs">{inv.brand} {inv.model}</td>
                        <td className="py-3 px-3 text-right">
                          <span className={`font-bold ${inv.cancelled ? 'line-through text-red-400' : 'text-white'}`}>{fmt(inv.amount)}</span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${sc.bg} ${sc.border} ${sc.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {sc.label}
                          </span>
                          {inv.payment_date && <div className="text-[10px] text-gray-500 mt-0.5">{formatDate(inv.payment_date)}</div>}
                          {inv.cancelled_by && <div className="text-[10px] text-red-400 mt-0.5">par {inv.cancelled_by}</div>}
                        </td>
                        <td className="py-3 px-3 hidden md:table-cell text-gray-500 text-xs">{formatDate(inv.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredInvoices.length === 0 && (
                <div className="text-center py-12 text-gray-500 text-sm">
                  {search || filterStatus !== 'all' ? 'Aucune facture correspondante' : 'Aucune facture B2B'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* UNINVOICED TAB */}
        {activeTab === 'uninvoiced' && (
          <div className="space-y-3">
            {uninvoiced_repairs.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm">Toutes les reparations avec prix sont facturees</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="uninvoiced-table">
                  <thead>
                    <tr className="border-b border-white/10 text-gray-500 text-xs uppercase tracking-wide">
                      <th className="text-left py-3 px-3">Ref.</th>
                      <th className="text-left py-3 px-3">Entreprise</th>
                      <th className="text-left py-3 px-3">Appareil</th>
                      <th className="text-left py-3 px-3">Statut</th>
                      <th className="text-right py-3 px-3">Montant</th>
                      <th className="text-left py-3 px-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uninvoiced_repairs.map(r => (
                      <tr key={r.repair_ref} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                        <td className="py-3 px-3"><span className="text-purple-400 font-mono text-xs">{r.repair_ref}</span></td>
                        <td className="py-3 px-3 text-gray-300 text-xs">{r.company_name}</td>
                        <td className="py-3 px-3 text-gray-400 text-xs">{r.brand} {r.model}</td>
                        <td className="py-3 px-3"><span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">{r.status}</span></td>
                        <td className="py-3 px-3 text-right font-bold text-white">{fmt(r.amount)}</td>
                        <td className="py-3 px-3 text-gray-500 text-xs">{formatDate(r.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* REMINDERS TAB */}
        {activeTab === 'reminders' && (
          <div className="space-y-4">
            {/* Header actions */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <button onClick={fetchReminders} disabled={remindersLoading}
                  className="flex items-center gap-1.5 px-3 py-2 bg-white/5 border border-white/10 text-gray-300 rounded-xl text-sm hover:bg-white/10 transition-colors disabled:opacity-50"
                  data-testid="reminders-refresh">
                  <RefreshCw className={`w-3.5 h-3.5 ${remindersLoading ? 'animate-spin' : ''}`} /> Actualiser
                </button>
                <span className="text-xs text-gray-500">
                  Factures en attente depuis plus de 10 jours
                </span>
              </div>
              {reminders.length > 0 && (
                <button onClick={sendAllReminders} disabled={sendingAll}
                  className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                  data-testid="send-all-reminders-btn">
                  {sendingAll ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  {sendingAll ? 'Envoi en cours...' : `Envoyer toutes les relances (${reminders.length})`}
                </button>
              )}
            </div>

            {/* Bulk result */}
            {reminderResult && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="reminder-result">
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="w-5 h-5 text-amber-400" />
                  <span className="text-white font-semibold text-sm">Resultat des relances</span>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="text-green-400">{reminderResult.sent} envoyee(s)</span>
                  {reminderResult.errors > 0 && <span className="text-red-400">{reminderResult.errors} erreur(s)</span>}
                  {reminderResult.skipped > 0 && <span className="text-gray-400">{reminderResult.skipped} ignoree(s)</span>}
                </div>
                {reminderResult.details?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {reminderResult.details.map((d, i) => (
                      <div key={i} className={`text-xs ${d.status === 'sent' ? 'text-green-400' : d.status === 'error' ? 'text-red-400' : 'text-gray-500'}`}>
                        {d.ref}: {d.status === 'sent' ? `Envoye a ${d.email}` : d.status === 'error' ? d.error : d.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Reminders list */}
            {remindersLoading ? (
              <div className="text-center py-12 text-gray-400 text-sm">Chargement...</div>
            ) : reminders.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">Aucune facture en retard de plus de 10 jours</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="reminders-table">
                  <thead>
                    <tr className="border-b border-white/10 text-gray-500 text-xs uppercase tracking-wide">
                      <th className="text-left py-3 px-3">Facture</th>
                      <th className="text-left py-3 px-3">Entreprise</th>
                      <th className="text-left py-3 px-3 hidden md:table-cell">Appareil</th>
                      <th className="text-right py-3 px-3">Montant</th>
                      <th className="text-center py-3 px-3">Retard</th>
                      <th className="text-center py-3 px-3">Relances</th>
                      <th className="text-center py-3 px-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reminders.map(r => (
                      <tr key={r.invoice_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`reminder-row-${r.invoice_number}`}>
                        <td className="py-3 px-3">
                          <span className="text-white font-mono font-semibold text-xs">{r.invoice_number}</span>
                          <div className="text-[10px] text-purple-400">{r.repair_ref}</div>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-gray-300 text-xs">{r.company_name}</span>
                          {r.company_email && <div className="text-[10px] text-gray-500">{r.company_email}</div>}
                        </td>
                        <td className="py-3 px-3 hidden md:table-cell text-gray-400 text-xs">{r.brand} {r.model}</td>
                        <td className="py-3 px-3 text-right font-bold text-white">{fmt(r.amount)}</td>
                        <td className="py-3 px-3 text-center">
                          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${r.days_pending > 30 ? 'bg-red-500/20 text-red-400 border border-red-500/30' : r.days_pending > 15 ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'}`}>
                            {r.days_pending}j
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className="text-xs text-gray-400">{r.reminders_sent}x</span>
                          {r.last_reminder && <div className="text-[10px] text-gray-600">{formatDate(r.last_reminder)}</div>}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <button onClick={() => sendReminder(r.invoice_id)} disabled={sendingReminder === r.invoice_id || !r.company_email}
                            className="flex items-center gap-1 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 text-amber-300 rounded-lg text-xs font-medium hover:bg-amber-500/30 transition-colors disabled:opacity-40 mx-auto"
                            title={!r.company_email ? 'Email entreprise non disponible' : 'Envoyer un rappel'}
                            data-testid={`send-reminder-${r.invoice_id}`}>
                            {sendingReminder === r.invoice_id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            Relancer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default B2BPaymentsPage;
